import { useState, useEffect, useCallback } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Reveal, Overline } from "@/components/Reveal";

export const Platform = () => {
  const { t } = useLang();
  const items = t.platform.items || [];
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, align: "start" });
  const [selected, setSelected] = useState(0);

  const onSelect = useCallback(() => {
    if (emblaApi) setSelected(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
  }, [emblaApi, onSelect]);

  return (
    <section id="platform" data-testid="platform-section" className="relative py-24 md:py-40 bg-[#070707]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="mb-12 md:mb-16 max-w-4xl">
          <Reveal><Overline>{t.platform.overline}</Overline></Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display uppercase text-5xl sm:text-7xl lg:text-8xl leading-[0.85] mt-4">
              {t.platform.title}
            </h2>
          </Reveal>
          {t.platform.intro && (
            <Reveal delay={0.2}>
              <p className="text-white/60 text-lg mt-6 font-light max-w-2xl leading-relaxed">
                {t.platform.intro}
              </p>
            </Reveal>
          )}
        </div>

        <Reveal delay={0.1}>
          <div className="relative">
            <div ref={emblaRef} className="overflow-hidden" data-testid="platform-carousel">
              <div className="flex">
                {items.map((item, i) => (
                  <div key={i} className="flex-[0_0_100%] min-w-0 pr-2" data-testid={`platform-slide-${i}`}>
                    <div className="grid lg:grid-cols-12 gap-6 lg:gap-10 border border-white/10 rounded-2xl p-6 md:p-10 bg-white/[0.02] min-h-[380px]">
                      <div className="lg:col-span-4 relative">
                        <div className="font-display leading-[0.8] stroke-purple select-none text-[6rem] md:text-[9rem] lg:text-[11rem]">
                          {String(i + 1).padStart(2, "0")}
                        </div>
                        <div className="text-xs tracking-[0.3em] uppercase text-purple-400 mt-2">
                          Commitment {String(i + 1).padStart(2, "0")}
                        </div>
                      </div>
                      <div className="lg:col-span-8">
                        <h3 className="font-display uppercase text-2xl md:text-4xl tracking-tight">{item.t}</h3>
                        <p className="text-lg md:text-xl font-light text-white/85 mt-4 leading-snug">{item.lead}</p>
                        <div className="mt-5 space-y-3">
                          {item.body.split("\n").map((p, j) => p.trim() && (
                            <p key={j} className="text-sm md:text-base text-white/55 leading-relaxed">{p.trim()}</p>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-8 flex items-center justify-between gap-4">
              <div className="flex items-center gap-1.5">
                {items.map((_, i) => (
                  <button
                    key={i}
                    data-testid={`platform-dot-${i}`}
                    aria-label={`Go to commitment ${i + 1}`}
                    onClick={() => emblaApi?.scrollTo(i)}
                    className={`h-1 rounded-full transition-all ${selected === i ? "w-10 bg-purple-500" : "w-6 bg-white/20 hover:bg-white/40"}`}
                  />
                ))}
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-white/50 tabular-nums">
                  <span className="text-white">{String(selected + 1).padStart(2, "0")}</span> / {String(items.length).padStart(2, "0")}
                </span>
                <button
                  data-testid="platform-prev"
                  onClick={() => emblaApi?.scrollPrev()}
                  aria-label="Previous commitment"
                  className="w-11 h-11 rounded-full border border-white/20 hover:border-purple-500 hover:text-purple-300 flex items-center justify-center transition-colors"
                >
                  <ArrowLeft size={18} />
                </button>
                <button
                  data-testid="platform-next"
                  onClick={() => emblaApi?.scrollNext()}
                  aria-label="Next commitment"
                  className="w-11 h-11 rounded-full border border-white/20 hover:border-purple-500 hover:text-purple-300 flex items-center justify-center transition-colors"
                >
                  <ArrowRight size={18} />
                </button>
              </div>
            </div>
          </div>
        </Reveal>

        {t.platform.approachBody && (
          <div className="mt-24 md:mt-32 pt-14 border-t border-white/10 grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-4">
              <Reveal><Overline>{t.platform.approachTitle}</Overline></Reveal>
            </div>
            <div className="lg:col-span-8 space-y-4">
              {t.platform.approachBody.split("\n").map((p, j) => p.trim() && (
                <Reveal key={j} delay={j * 0.05}>
                  <p className="text-base md:text-lg text-white/70 leading-relaxed">{p.trim()}</p>
                </Reveal>
              ))}
            </div>
          </div>
        )}

        {t.platform.closing && (
          <Reveal delay={0.2}>
            <p className="font-display uppercase text-3xl md:text-5xl lg:text-6xl text-center mt-20 leading-tight">
              <span className="text-purple-400">{t.platform.closing}</span>
            </p>
          </Reveal>
        )}
      </div>
    </section>
  );
};
